"use client";

// useConnectWalks — the connect walk's hands, held by netti's rail (NettiDock).
//
// The words and the order live in lib/connect/walk (pure, tested). This hook does
// what the person can feel (John, 2026-10-27, second pass: "it gives you the first
// step, not all the steps at once; a very simple, intuitive, holistic experience"):
//   · START — a press anywhere (connectWithNetti) or a tool named in the rail opens
//     a FRESH conversation with netti. A new connection is NAMED first, in the chat:
//     netti asks, the person answers in the composer or taps the name netti offers.
//   · THE WAY IN — the quickest first: one command (`npx innernet`) for every tool
//     it wires, the browser extension for the apps it can set up, or by hand. By
//     hand is ONE step at a time: the words, the real screen, the thing to paste on
//     the step that needs it, and one button.
//   · LISTEN — while a walk waits for a call, /api/connections is read every few
//     seconds; the first call (on any way, at any step) moves the walk on. An arrival
//     that lands as a row of its own is taken as this walk's, as the sheet does
//     (lib/connect/setup arrivalOf): it takes the name given here and the waiting
//     row is let go. A machine `npx innernet` already wired is recognised.
//   · THE INSTRUCTION CHECK — once the person says it is pasted, a call from then on
//     is the tool reaching for innernet from the new chat.
//   · HEAR — a typed or tapped line is read for what it asks of the walk; what it
//     covers is answered here, a real question goes to netti itself (the dock).

import * as React from "react";
import type { ChatCard } from "../NettiChat";
import type { Connection } from "../../../lib/connect/connections";
import { LIVE_WINDOW_MS, connState, suggestLabel } from "../../../lib/connect/connections";
import { type ConnectTool } from "../../../lib/connect/tools";
import { renameConnection } from "../../../lib/connect/api";
import { arrivalBaseline, arrivalOf, type ArrivalBaseline } from "../../../lib/connect/setup";
import { connectTokens, resolveClient } from "../../../lib/connect/resolve";
import { CONNECT_SHEET_EVENT, CONNECT_WALK_DONE_EVENT, type NettiConnectAsk } from "../../../lib/connect/nettiConnect";
import {
  IS_IT_SAFE,
  LATER_LINE,
  NO_MCP,
  NO_NODE,
  PARTIAL_LEAD,
  PREVIEW_FALLBACK,
  WHAT_IMPORT_BRINGS,
  WHAT_IS_MCP,
  WHY_INSTRUCTION,
  appWord,
  cleanWalk,
  handSteps,
  keepWalks,
  lineAlready,
  lineArrived,
  lineCoach,
  lineDone,
  lineHandStep,
  lineHeard,
  lineImport,
  lineInstructions,
  lineListenInstructions,
  lineListening,
  lineName,
  lineNpx,
  linePick,
  lineStart,
  lineStatus,
  lineTrust,
  lineWay,
  linePlugin,
  nextStep,
  npxSeen,
  planLine,
  readIntent,
  stepsFor,
  suggestNext,
  toolWord,
  troubleFor,
  tryItAsk,
  walkId,
  waysFor,
  walkTool,
  type Intent,
  type Said,
  type Walk,
  type WalkStep,
  type WalkWay,
} from "../../../lib/connect/walk";
import { copyMixQuietly } from "../../../lib/identity/client";
import { relTime } from "../../../lib/os/tree";
import { trackFunnel, EVENTS } from "../../../lib/analytics";

const STORE = "innernet:netti-connect:walks";
const FLOW = "netti_walk";
/** how often the rail reads the connections while a walk is listening */
const LISTEN_MS = 3000;
/** …and while the rail is up with nothing listening */
const IDLE_MS = 30_000;
/** a line longer than this is a question for netti where netti can answer it */
const SHORT_WORDS = 6;
/** a walk's row missing from a read this long after it was set is gone, not merely not read yet */
const GONE_AFTER_MS = 10_000;
/** a walk touched longer ago than this is not "the one we're on" for a typed line */
const CURRENT_MS = 3 * 3600_000;

export interface WalkSpeech {
  speak: (text: string, extra?: { card?: ChatCard; replies?: string[] }) => Promise<string>;
  post: (text: string, extra?: { via?: "chip" }) => string;
  /** the newest card in the conversation, so a second press on the same step says nothing twice */
  lastCard: () => ChatCard | null;
  /** a fresh conversation with netti: a new connection starts one */
  fresh: () => void;
}

/** the browser extension, as the walk can use it */
export interface WalkCoach {
  /** may this person have the extension at all (F.browserExtension)? */
  offered: boolean;
  /** is it in this browser, able to set this tool up? */
  can: (toolId: string) => boolean;
  /** open the tool's own page with the extension's card */
  run: (toolId: string) => Promise<boolean>;
}

export interface ConnectWalks {
  walks: Walk[];
  conns: Connection[];
  loaded: boolean;
  now: number;
  /** a walk's connection is being made (or failed to be) */
  making: Record<string, "making" | "failed">;
  start: (ask: NettiConnectAsk) => void;
  /** the walk the rail is on: the newest one not done, touched lately */
  current: () => Walk | null;
  advance: (id: string, opts?: { skipped?: boolean; lead?: string }) => void;
  rename: (id: string, label: string) => Promise<boolean>;
  /** the name the person gave a new connection: it is made, and the way in follows */
  named: (id: string, label: string) => void;
  /** the way in, chosen */
  choose: (id: string, way: WalkWay) => void;
  /** by hand: this step is done */
  stepDone: (id: string) => void;
  pasted: (id: string) => void;
  retryMake: (id: string) => void;
  pick: () => void;
  /** a press on one tool (in the pick card, done's next tools) */
  focusTool: (toolId: string) => void;
  /** a typed or tapped line: true when the walk answered it. `generous` where no model can answer
   *  instead (ask netti is not open, or resting): then a longer line is matched too. */
  hear: (text: string, via?: "chip", generous?: boolean) => boolean;
  /** a line nothing here answers and no model may: netti says so plainly */
  fallback: (text: string, via?: "chip") => void;
  /** the walk open now, for netti's own context (ids and fixed words only) */
  context: () => { tool: string; step: WalkStep; state: "live" | "waiting" } | null;
  connOf: (w: Walk) => Connection | null;
  /** the name netti offers for a new connection of this tool */
  suggested: (toolId: string) => string;
  coach: WalkCoach;
}

function readStore(): Walk[] {
  try {
    const raw = localStorage.getItem(STORE);
    const list = raw ? (JSON.parse(raw) as unknown[]) : [];
    return Array.isArray(list) ? keepWalks(list.map(cleanWalk).filter((w): w is Walk => !!w)) : [];
  } catch {
    return [];
  }
}

async function readConns(): Promise<Connection[] | null> {
  try {
    const r = await fetch("/api/connections", { cache: "no-store" });
    if (!r.ok) return null;
    return (((await r.json().catch(() => ({}))) as { connections?: Connection[] }).connections ?? []);
  } catch {
    return null;
  }
}

const pause = (ms: number) => new Promise((r) => setTimeout(r, ms));
const without = <T,>(m: Record<string, T>, id: string): Record<string, T> => {
  const next = { ...m };
  delete next[id];
  return next;
};
const newestFirst = (a: Connection, b: Connection) => (b.created_at ?? "").localeCompare(a.created_at ?? "");
const lastSeenFirst = (a: Connection, b: Connection) => (b.last_seen_at ?? b.created_at ?? "").localeCompare(a.last_seen_at ?? a.created_at ?? "");

/** a said line's card */
const card = (w: Walk, step: WalkStep = w.step): ChatCard => ({ kind: "connect", walk: w.id, step });

export function useConnectWalks({ enabled, speech, railUp, openRail, accountName, coach }: {
  enabled: boolean;
  speech: WalkSpeech;
  railUp: boolean;
  /** open (and keep) the rail: a walk began or was taken up */
  openRail: () => void;
  /** the name the person goes by, for the connection name netti offers */
  accountName?: string | null;
  coach: WalkCoach;
}): ConnectWalks {
  const [walks, setWalks] = React.useState<Walk[]>([]);
  const [conns, setConns] = React.useState<Connection[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [now, setNow] = React.useState(() => Date.now());
  const [making, setMaking] = React.useState<Record<string, "making" | "failed">>({});
  const walksRef = React.useRef<Walk[]>([]);
  const connsRef = React.useRef<Connection[]>([]);
  const speechRef = React.useRef(speech);
  const coachRef = React.useRef(coach);
  React.useLayoutEffect(() => {
    walksRef.current = walks;
    connsRef.current = conns;
    speechRef.current = speech;
    coachRef.current = coach;
  });

  // ── keep ────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!enabled) return;
    const kept = readStore();
    walksRef.current = kept;
    // the walks live in this browser: read once the page is in it (the server render has none)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setWalks(kept);
  }, [enabled]);

  const commit = React.useCallback((next: Walk[]) => {
    const kept = keepWalks(next);
    walksRef.current = kept;
    setWalks(kept);
    try { localStorage.setItem(STORE, JSON.stringify(kept)); } catch { /* private mode: this visit only */ }
  }, []);

  const patch = React.useCallback((id: string, p: Partial<Walk>) => {
    commit(walksRef.current.map((w) => (w.id === id ? { ...w, ...p, touched: Date.now() } : w)));
  }, [commit]);

  const walkById = (id: string) => walksRef.current.find((w) => w.id === id) ?? null;
  const toolOf = (w: Walk): ConnectTool => walkTool(w.tool) ?? walkTool("other")!;
  const connOf = React.useCallback((w: Walk) => (w.conn ? connsRef.current.find((c) => c.id === w.conn) ?? null : null), []);

  const say = React.useCallback((s: Said, extra: { card?: ChatCard } = {}) => {
    void speechRef.current.speak(s.text, { ...(extra.card ? { card: extra.card } : {}), ...(s.replies ? { replies: s.replies } : {}) });
  }, []);

  const suggested = React.useCallback((toolId: string) => suggestLabel(toolId, accountName ?? null, connsRef.current), [accountName]);

  // ── read the connections ────────────────────────────────────────────────
  const loading = React.useRef(false);
  const load = React.useCallback(async (): Promise<Connection[] | null> => {
    if (loading.current) return null;
    loading.current = true;
    try {
      const rows = await readConns();
      if (rows) {
        connsRef.current = rows;
        setConns(rows);
        setLoaded(true);
      }
      return rows;
    } finally {
      loading.current = false;
    }
  }, []);
  // the rail opening reads them, so a pick or a name is offered against what is there
  React.useEffect(() => {
    if (enabled && railUp) void load();
  }, [enabled, railUp, load]);

  // ── make (or take up) the connection a walk wires, under the name it was given ──
  const makeFor = React.useCallback(async (id: string) => {
    const w0 = walkById(id);
    if (!w0 || w0.conn) return;
    setMaking((m) => ({ ...m, [id]: "making" }));
    const before = await readConns();
    // a waiting row for this tool is taken up (and given this name), never a second one made beside it
    const waiting = (before ?? []).filter((c) => c.tool_id === w0.tool && !c.last_seen_at).sort(newestFirst)[0];
    if (waiting) {
      const renamed = waiting.label !== w0.label ? await renameConnection(waiting.id, w0.label) : true;
      patch(id, { conn: waiting.id, label: renamed ? w0.label : waiting.label });
      setMaking((m) => without(m, id));
      void load();
      return;
    }
    const had = new Set((before ?? []).map((c) => c.id));
    for (let attempt = 0; attempt < 4; attempt++) {
      const res = await fetch("/api/connections", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool_id: w0.tool, label: w0.label }),
      }).catch(() => null);
      const data = res ? ((await res.json().catch(() => ({}))) as { connection?: Connection }) : {};
      if (res?.ok && data.connection) {
        patch(id, { conn: data.connection.id, label: data.connection.label, made: true });
        setMaking((m) => without(m, id));
        trackFunnel(EVENTS.onboarding_step_completed, { step: `mint.${w0.tool}`, index: 0, flow: FLOW, client: w0.tool });
        void load();
        return;
      }
      if (res && res.status < 500) break; // signed out, an unknown tool: asking again changes nothing
      // an answer lost on the way: look before making a second one
      await pause(900 * 2 ** attempt);
      const now = before ? await readConns() : null;
      const landed = now?.filter((c) => !had.has(c.id) && c.tool_id === w0.tool).sort(newestFirst)[0];
      if (landed) {
        patch(id, { conn: landed.id, label: landed.label, made: true });
        setMaking((m) => without(m, id));
        return;
      }
    }
    setMaking((m) => ({ ...m, [id]: "failed" }));
    trackFunnel(EVENTS.connect_failed, { client: w0.tool, reason: "mint", flow: FLOW });
  }, [patch, load]);

  // ── the walk the rail is on ───────────────────────────────────────────────
  const current = React.useCallback((): Walk | null => {
    const t = Date.now();
    return [...walksRef.current]
      .filter((w) => w.step !== "done" && t - w.touched < CURRENT_MS)
      .sort((a, b) => b.touched - a.touched)[0] ?? null;
  }, []);

  /** the ways in this tool has for this person, the quickest first */
  const waysOf = React.useCallback((t: ConnectTool) => waysFor(t, { coach: coachRef.current.offered || coachRef.current.can(t.id) }), []);

  /** what netti says for the wire step where the walk stands on it */
  const wireLine = React.useCallback((w: Walk, t: ConnectTool): Said => {
    if (!w.way) {
      const npx = npxSeen(connsRef.current, t.id);
      return lineWay(t, waysOf(t), { npxBefore: npx.machines > 0, extension: coachRef.current.can(t.id) });
    }
    if (w.way === "plugin") return linePlugin(t);
    if (w.way === "npx") return lineNpx(t);
    if (w.way === "coach") return lineCoach(t, true);
    const steps = handSteps(t);
    if (w.at >= steps.length) return lineListening(t);
    return lineHandStep(t, w.at, steps.length, steps[w.at].text);
  }, [waysOf]);

  /** say the line for a step and hang its card. `lead` is what just happened (a call landed, a
   *  step was passed over), said in the same breath, so netti never says two lines where one will do. */
  const sayStep = React.useCallback((w: Walk, step: WalkStep, lead?: string) => {
    const t = toolOf(w);
    const lines: Record<WalkStep, () => Said> = {
      name: () => lineName(t, suggested(t.id)),
      wire: () => wireLine(w, t),
      instructions: () => lineInstructions(t),
      trust: () => lineTrust(t),
      import: () => lineImport(t),
      done: () => {
        const have = new Set(connsRef.current.filter((c) => c.last_seen_at).map((c) => c.tool_id));
        have.add(t.id);
        return lineDone(t, have.size, suggestNext(have));
      },
    };
    const line = lines[step]();
    // the walk's end is said aloud: the first-run walk, waiting on the connect step, takes it from here
    // the steps it walked ride along: the first-run walk never asks again for one the chat already took
    if (step === "done") window.dispatchEvent(new CustomEvent(CONNECT_WALK_DONE_EVENT, { detail: { tool: t.id, id: w.conn, steps: stepsFor(t) } }));
    // the name is asked in the conversation itself: no card under the question
    say(lead ? { ...line, text: `${lead} ${line.text}` } : line, step === "name" ? {} : { card: card(w, step) });
  }, [say, suggested, wireLine]);

  const advance = React.useCallback((id: string, opts: { skipped?: boolean; lead?: string } = {}) => {
    const w = walkById(id);
    if (!w) return;
    const t = toolOf(w);
    const to = nextStep(t, w.step);
    const skipped = opts.skipped ? [...new Set([...w.skipped, w.step])] : w.skipped;
    patch(id, { step: to, skipped });
    trackFunnel(EVENTS.onboarding_step_completed, { step: `walk.${w.step}`, index: stepsFor(t).indexOf(w.step), flow: FLOW, client: t.id, ...(opts.skipped ? { skipped: true } : {}) });
    sayStep({ ...w, step: to }, to, opts.lead);
  }, [patch, sayStep]);

  /** where a wire step begins: the way in asked when there is a choice, else the only way */
  const beginWire = React.useCallback((w: Walk, lead?: string) => {
    const t = toolOf(w);
    const ways = waysOf(t);
    const way = w.way ?? (ways.length === 1 ? ways[0] : null);
    const next = { ...w, step: "wire" as const, way, at: w.way ? w.at : 0 };
    patch(w.id, { step: "wire", way, at: next.at });
    sayStep(next, "wire", lead);
  }, [patch, sayStep, waysOf]);

  // ── start ────────────────────────────────────────────────────────────────
  const start = React.useCallback((ask: NettiConnectAsk) => {
    openRail();
    const hit = ask.tool ? { tool: walkTool(ask.tool) } : ask.what?.trim() ? resolveClient(ask.what) : null;
    const t = hit?.tool ? walkTool(hit.tool.id) : null;
    if (!t) {
      speechRef.current.fresh();
      say(linePick(connsRef.current.filter((c) => c.last_seen_at).length), { card: { kind: "connect-pick" } });
      return;
    }
    const unknownLead = hit && "unknown" in hit && hit.unknown && ask.what?.trim()
      ? `i don't have ${ask.what.trim().slice(0, 60)} written down, but that's all right.`
      : undefined;
    trackFunnel(EVENTS.connect_client_selected, { client: t.id, flow: FLOW, via: ask.from });

    const all = connsRef.current;
    const conn = ask.connection ? all.find((c) => c.id === ask.connection) ?? null : null;
    // a walk already on this tool (or this very connection) is taken up, not begun again
    const open = walksRef.current.find((w) => w.tool === t.id && (ask.connection ? w.conn === ask.connection : w.step !== "done"));
    if (open) {
      patch(open.id, {});
      // its card is already the newest thing in the rail: opening the rail is the whole answer
      const last = speechRef.current.lastCard();
      if (last?.kind === "connect" && last.walk === open.id && last.step === open.step) return;
      speechRef.current.fresh();
      const live = !!connOf(open)?.last_seen_at;
      sayStep(open, open.step, lineStart(t, { resumed: !live, connected: live && open.step === "wire" }).text);
      return;
    }
    // asked for a tool that is already connected, from anywhere but a press meant to add one: ask first
    const seenRow = !conn && !["list", "orbit", "another"].includes(ask.from)
      ? all.filter((c) => c.tool_id === t.id && c.last_seen_at).sort(lastSeenFirst)[0]
      : null;
    if (seenRow) {
      say(lineAlready(t, seenRow.label, seenRow.last_seen_at ? relTime(seenRow.last_seen_at) : null));
      return;
    }

    speechRef.current.fresh();
    const at = Date.now();
    const seen = !!conn?.last_seen_at;
    const want = ask.step === "instructions" || ask.step === "trust" || ask.step === "import" ? (ask.step as WalkStep) : null;
    const steps = stepsFor(t);
    // a connection pressed in a list already has its name: it is wired (or finished), never named again
    const step: WalkStep = conn ? (seen ? (want && steps.includes(want) ? want : "instructions") : "wire") : "name";
    const w: Walk = {
      id: walkId(at),
      tool: t.id,
      conn: conn?.id ?? null,
      label: conn?.label ?? suggested(t.id),
      step,
      way: null,
      at: 0,
      arrived: seen ? conn!.last_seen_at : null,
      heardFrom: null,
      heard: null,
      skipped: [],
      from: ask.from.slice(0, 24),
      started: at,
      touched: at,
    };
    commit([w, ...walksRef.current]);
    if (seen) sayStep(w, step, lineStart(t, { connected: true }).text);
    else if (step === "wire") beginWire(w, unknownLead);
    else sayStep(w, "name", unknownLead);
  }, [openRail, say, patch, connOf, commit, sayStep, beginWire, suggested]);

  // ── the name, the way, the steps ────────────────────────────────────────
  const named = React.useCallback((id: string, label: string) => {
    const w = walkById(id);
    const next = label.trim().replace(/^["'“]|["'”.]+$/g, "").slice(0, 80);
    if (!w || w.step !== "name" || !next) return;
    const w2 = { ...w, label: next };
    patch(id, { label: next });
    void makeFor(id);
    beginWire(w2, `**${next}** it is.`);
  }, [patch, makeFor, beginWire]);

  const choose = React.useCallback((id: string, way: WalkWay) => {
    const w = walkById(id);
    if (!w || w.step !== "wire") return;
    const t = toolOf(w);
    if (way === "coach") {
      const c = coachRef.current;
      if (!c.can(t.id)) {
        // not in this browser yet: its page opens beside the dashboard, and the walk waits here
        if (typeof window !== "undefined") window.open("/connect/extension", "_blank", "noopener");
        say({ text: `the extension's page is open in a new tab. once it's added, tap "let the extension do it" here, or i can walk you through ${appWord(t)} by hand.`, replies: ["walk me through it"] });
        return;
      }
      patch(id, { way: "coach" });
      void c.run(t.id).then((ok) => {
        trackFunnel(EVENTS.connect_instructions_copied, { what: ok ? "coach" : "coach_failed", flow: FLOW });
        if (ok) say(lineCoach(t, true), { card: card({ ...w, way: "coach" }) });
        else { patch(id, { way: "hand", at: 0 }); sayStep({ ...w, way: "hand", at: 0 }, "wire", lineCoach(t, false).text); }
      });
      return;
    }
    const next = { ...w, way, at: 0 };
    patch(id, { way, at: 0 });
    sayStep(next, "wire");
  }, [patch, say, sayStep]);

  const stepDone = React.useCallback((id: string) => {
    const w = walkById(id);
    if (!w || w.step !== "wire" || w.way !== "hand") return;
    const n = handSteps(toolOf(w)).length;
    // past the last step the walk listens: that step is the one that makes the call
    const next = { ...w, at: Math.min(w.at + 1, n) };
    patch(id, { at: next.at });
    sayStep(next, "wire");
  }, [patch, sayStep]);

  // ── listen ───────────────────────────────────────────────────────────────
  const listening = walks.some((w) => (w.step === "wire" && !w.arrived && !!w.conn) || (w.heardFrom !== null && !w.heard && w.step === "instructions"));
  React.useEffect(() => {
    if (!enabled) return;
    if (!listening && !railUp) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      if (!alive) return;
      if (document.visibilityState === "visible") await load();
      if (alive) timer = setTimeout(tick, listening ? LISTEN_MS : IDLE_MS);
    };
    timer = setTimeout(tick, listening ? LISTEN_MS : IDLE_MS);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [enabled, listening, railUp, load]);

  // a clock, so "still listening" and "live" move on their own
  React.useEffect(() => {
    if (!enabled || (!railUp && !listening)) return;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [enabled, railUp, listening]);

  // every new read: did a walk's tool call?
  const baselines = React.useRef(new Map<string, ArrivalBaseline>());
  const handled = React.useRef(new Set<string>());
  React.useEffect(() => {
    if (!enabled || !loaded) return;
    for (const w of walksRef.current) {
      const t = toolOf(w);
      // 0 — the row it was wiring is gone (let go from the list, or never kept): take up or make another.
      // Only on a read well after the row was set on the walk (`touched`), so a poll that left before
      // the row was made can never make a second one.
      if (w.step === "wire" && !w.arrived && w.conn && making[w.id] === undefined && Date.now() - w.touched > GONE_AFTER_MS && !conns.some((c) => c.id === w.conn) && !handled.current.has(`gone:${w.conn}`)) {
        handled.current.add(`gone:${w.conn}`);
        baselines.current.delete(w.id);
        patch(w.id, { conn: null, made: false });
        void makeFor(w.id);
        continue;
      }
      // 1 — the first call, on whatever step the walk is (a person who skipped ahead is still heard)
      if (!w.arrived && w.conn) {
        if (!baselines.current.has(w.id)) baselines.current.set(w.id, arrivalBaseline(conns));
        let mine = conns.find((c) => c.id === w.conn) ?? null;
        if (!mine?.last_seen_at) {
          const others = conns.filter((c) => c.tool_id === w.tool && c.id !== w.conn);
          const came = arrivalOf(baselines.current.get(w.id)!, others);
          if (came?.tier === 1 && !handled.current.has(`swap:${w.id}`)) {
            handled.current.add(`swap:${w.id}`);
            const row = came.row;
            const waitingId = w.conn;
            void (async () => {
              const renamed = row.adopted_at && row.label !== w.label ? await renameConnection(row.id, w.label) : false;
              await copyMixQuietly(`connection:${row.id}`, `connection:${waitingId}`).catch(() => false);
              await fetch(`/api/connections?id=${encodeURIComponent(waitingId)}`, { method: "DELETE" }).catch(() => undefined);
              patch(w.id, { conn: row.id, label: renamed ? w.label : row.label });
              void load();
            })();
            continue;
          }
        }
        if (mine?.last_seen_at && !handled.current.has(`arrive:${w.id}`)) {
          handled.current.add(`arrive:${w.id}`);
          trackFunnel(EVENTS.connect_verified, { client: t.id, via: "netti_walk", flow: FLOW });
          window.dispatchEvent(new CustomEvent("innernet:connected", { detail: { tool: t.id, id: mine.id } }));
          if (w.step === "wire") {
            const to = nextStep(t, "wire");
            patch(w.id, { arrived: mine.last_seen_at, step: to, label: mine.label });
            sayStep({ ...w, step: to, label: mine.label }, to, lineArrived(t, mine.label).text);
          } else {
            // heard after the person moved on: the walk stays where it is. The instruction check, when
            // it is listening, says it in its own words below; otherwise netti says the call landed.
            patch(w.id, { arrived: mine.last_seen_at, label: mine.label });
            const fresh = Date.now() - Date.parse(mine.last_seen_at) < LIVE_WINDOW_MS * 3;
            if (w.heardFrom === null && fresh && w.step !== "done" && w.step !== "name") say(lineArrived(t, mine.label));
          }
          mine = null;
        }
      }
      // 2 — the instruction check: a call after it was pasted
      if (w.heardFrom !== null && !w.heard && w.conn) {
        const c = conns.find((x) => x.id === w.conn);
        if (c && c.call_count > w.heardFrom && !handled.current.has(`heard:${w.id}`)) {
          handled.current.add(`heard:${w.id}`);
          patch(w.id, { heard: c.last_seen_at ?? new Date().toISOString() });
          if (w.step === "instructions") advance(w.id, { lead: lineHeard(t).text });
          else say(lineHeard(t));
        }
      }
    }
  }, [enabled, loaded, conns, making, patch, say, sayStep, advance, load, makeFor]);

  // ── the small hands on a card ─────────────────────────────────────────────
  const rename = React.useCallback(async (id: string, label: string) => {
    const w = walkById(id);
    const next = label.trim().slice(0, 80);
    if (!w || !next) return false;
    if (!w.conn) { patch(id, { label: next }); return true; }
    const ok = await renameConnection(w.conn, next);
    if (ok) { patch(id, { label: next }); void load(); }
    return ok;
  }, [patch, load]);

  const pasted = React.useCallback((id: string) => {
    const w = walkById(id);
    if (!w) return;
    const c = connOf(w);
    trackFunnel(EVENTS.connect_instructions_copied, { what: "instruction_pasted", flow: FLOW });
    // a call from now on is the tool reaching for innernet from the new chat (the first call, too,
    // for someone who skipped ahead before it had called)
    patch(id, { heardFrom: c?.call_count ?? 0, heard: null });
    say(lineListenInstructions(toolOf(w)), { card: card(w, "instructions") });
  }, [connOf, patch, say]);

  const retryMake = React.useCallback((id: string) => { void makeFor(id); }, [makeFor]);

  const pick = React.useCallback(() => {
    openRail();
    say(linePick(connsRef.current.filter((c) => c.last_seen_at).length), { card: { kind: "connect-pick" } });
  }, [openRail, say]);

  const openSheet = React.useCallback((connId: string) => {
    window.dispatchEvent(new CustomEvent(CONNECT_SHEET_EVENT, { detail: { id: connId } }));
  }, []);

  // the tool netti last said where things stand for: "open its settings" means that one
  const aboutTool = React.useRef<string | null>(null);
  const focusTool = React.useCallback((toolId: string) => {
    aboutTool.current = toolId;
    const open = walksRef.current.find((w) => w.tool === toolId && w.step !== "done");
    if (open) { start({ tool: toolId, from: "tracker" }); return; }
    const t = walkTool(toolId);
    const mine = connsRef.current.filter((c) => c.tool_id === toolId).sort(newestFirst);
    if (!t || !mine.length) { start({ tool: toolId, from: "pick" }); return; }
    // only waiting ones: the newest is taken up and walked, never asked about
    if (!mine.some((c) => c.last_seen_at)) { start({ tool: toolId, connection: mine[0].id, from: "pick" }); return; }
    openRail();
    const rows = mine.slice(0, 3).map((c) => ({ name: c.label, state: stateWord(c, Date.now()) }));
    say({ text: lineStatus(rows), replies: ["open its settings", `connect another ${toolWord(t)}`] });
  }, [start, openRail, say]);

  // ── hear ─────────────────────────────────────────────────────────────────
  const respond = React.useCallback((intent: Intent, partial = false) => {
    const w = current();
    const t = w ? toolOf(w) : null;
    // an answer from the walk's own words to a longer question no model can take here: said as the
    // closest thing netti has, never as if it were the whole answer
    const info = (s: Said) => say(partial ? { ...s, text: `${PARTIAL_LEAD}\n\n${s.text}` } : s);
    switch (intent.kind) {
      case "tool": {
        // "actually claude code": the walk just begun for its sibling is let go, its unused door with it
        if (w && w.tool !== intent.tool && (w.step === "name" || w.step === "wire") && !w.arrived) {
          if (w.made && w.conn) void fetch(`/api/connections?id=${encodeURIComponent(w.conn)}`, { method: "DELETE" }).catch(() => undefined);
          commit(walksRef.current.filter((x) => x.id !== w.id));
        }
        start({ tool: intent.tool, from: intent.another ? "another" : "typed" });
        return;
      }
      case "finish": {
        // the newest connection of that tool, walked from where its setup stands
        const c = connsRef.current.filter((x) => x.tool_id === intent.tool).sort(newestFirst)[0];
        start({ tool: intent.tool, connection: c?.id ?? null, from: "typed" });
        return;
      }
      case "rename":
        if (!w) { say({ text: "tell me which connection first, then what to call it." }); return; }
        void rename(w.id, intent.label).then((ok) =>
          say({ text: ok ? `it's called **${intent.label}** now.` : "i couldn't rename it just now. try once more in a moment." }));
        return;
      case "another":
        pick();
        return;
      case "status": {
        aboutTool.current = null;
        const rows = connsRef.current.slice(0, 6).map((c) => ({ name: c.label, state: stateWord(c, Date.now()) }));
        say({ text: lineStatus(rows), replies: ["what else can i connect?"] });
        return;
      }
      case "mcp": info({ text: WHAT_IS_MCP }); return;
      case "safe": info({ text: IS_IT_SAFE }); return;
      case "nomcp": info({ text: NO_MCP, replies: ["connect Claude", "connect ChatGPT"] }); return;
      case "nonode": info({ text: NO_NODE, replies: ["i’d rather do it by hand", "connect Claude"] }); return;
      case "imports": info({ text: WHAT_IMPORT_BRINGS }); return;
      case "plan":
        info({ text: t ? planLine(t) : "every plan that can add an mcp connector works. tell me which app and i'll say what it needs." });
        return;
      case "try": {
        const tt = t ?? walkTool(connsRef.current.find((c) => c.last_seen_at)?.tool_id ?? "") ?? null;
        if (!tt) { say({ text: "connect something first, then ask it what it knows about you." }); return; }
        say({ text: `open a new chat in ${appWord(tt)} and ask:\n\n\`\`\`\n${tryItAsk(tt)}\n\`\`\`\n\nit should answer from your memory, and say it checked innernet.` });
        return;
      }
    }
    if (!w || !t) { pick(); return; }
    const steps = handSteps(t);
    switch (intent.kind) {
      case "way":
        if (w.step === "wire") choose(w.id, intent.way);
        else say({ text: `${toolWord(t)} is already past that step.` });
        return;
      case "back":
        if (w.step === "wire" && w.way === "hand" && w.at > 0) {
          const back = { ...w, at: Math.min(w.at, steps.length) - 1 };
          patch(w.id, { at: back.at });
          sayStep(back, "wire");
          return;
        }
        if (w.step === "wire" && w.way && waysOf(t).length > 1) { patch(w.id, { way: null, at: 0 }); sayStep({ ...w, way: null, at: 0 }, "wire"); return; }
        say({ text: "this is the first step." });
        return;
      case "restart": {
        speechRef.current.fresh();
        const back = { ...w, step: "wire" as const, way: null, at: 0, heardFrom: null };
        patch(w.id, { step: "wire", way: null, at: 0, heardFrom: null });
        beginWire(back);
        return;
      }
      case "why":
        info({ text: w.step === "trust" ? IS_IT_SAFE : WHY_INSTRUCTION });
        return;
      case "later":
        // on the bring-in step, later means "not this step": the walk goes on to its end
        if (w.step === "import") { advance(w.id, { skipped: true }); return; }
        say({ text: LATER_LINE });
        return;
      case "end":
        patch(w.id, { step: "done", skipped: [...new Set([...w.skipped, ...stepsFor(t).filter((s) => s !== "done" && s !== "name" && s !== w.step)])] });
        sayStep({ ...w, step: "done" }, "done");
        return;
      case "skip":
        if (w.step === "name") { named(w.id, w.label); return; }
        if (w.step === "instructions" && w.heardFrom !== null && !w.heard) { patch(w.id, { heardFrom: null }); advance(w.id); return; }
        advance(w.id, { skipped: true, ...(w.step === "wire" ? { lead: `moving on. i'll keep listening for ${toolWord(t)} and say when it calls.` } : {}) });
        return;
      case "done":
        if (w.step === "name") { named(w.id, w.label); return; }
        if (w.step === "wire") {
          if (w.way === "hand" && w.at < steps.length) { stepDone(w.id); return; }
          say({ text: `i haven't heard from ${toolWord(t)} yet. once it's signed in, start a new chat and say hi. i'm listening.`, replies: ["it isn’t showing up", "skip ahead"] });
          return;
        }
        if (w.step === "instructions" && w.heardFrom === null) { pasted(w.id); return; }
        if (w.step === "done") { pick(); return; }
        advance(w.id);
        return;
      case "stuck": {
        if (w.step === "instructions") {
          info({ text: `the instruction only reaches a NEW chat. if ${appWord(t)} still doesn't call innernet, check innernet is switched on in that chat's tools, and that the instruction is saved in ${t.promptWhere}.`, replies: ["skip the check"] });
          return;
        }
        const tips = troubleFor(t).map((s) => `- ${s}`).join("\n");
        info({ text: `a few things that usually do it:\n\n${tips}`, replies: w.way === "hand" ? ["go back a step", "skip ahead"] : w.way === "plugin" ? ["open the plugin again", "skip ahead"] : ["i’d rather do it by hand", "skip ahead"] });
        return;
      }
      case "where": {
        const shot = w.step === "wire" && w.way === "hand" ? steps[Math.min(w.at, steps.length - 1)]?.shot : null;
        if (shot) { info({ text: "the screen is on the card above, with the press marked." }); return; }
        if (w.step === "wire" && w.way === "plugin") { info({ text: `the button on the card above opens the innernet plugin's page. press Connect there and sign in.` }); return; }
        info({ text: w.step === "instructions" ? `it goes in ${t.promptWhere}.` : t.where ? `it goes in ${t.where}.` : "it goes in the app's mcp or connector settings." });
        return;
      }
    }
  }, [current, commit, start, rename, pick, patch, sayStep, advance, pasted, say, choose, stepDone, named, beginWire, waysOf]);

  const hear = React.useCallback((text: string, via?: "chip", generous = true): boolean => {
    if (!enabled) return false;
    const w = current();
    const s = text.trim().toLowerCase();
    // the name a new connection is asked for: whatever the person answers is its name, unless it is
    // plainly something else (a question, another tool, later, start over)
    if (w?.step === "name") {
      const intent = readIntent(text, true);
      const question = /\?\s*$/.test(text.trim());
      const other = !!intent && (intent.kind === "later" || intent.kind === "another" || intent.kind === "restart" || intent.kind === "status"
        || (intent.kind === "tool" && /^(actually|connect|instead|i meant|no,?)\b/i.test(text.trim())));
      if (!question && !other) {
        speechRef.current.post(text, via ? { via } : undefined);
        named(w.id, text);
        return true;
      }
    }
    // a long line with no walk open is a question for netti, not a connect word
    if (!w && connectTokens(text).length > SHORT_WORDS) return false;
    // the replies the walk itself offered
    if (s === "open its settings") {
      const about = aboutTool.current ?? w?.tool ?? null;
      const c = [...connsRef.current].filter((x) => !about || x.tool_id === about).sort(lastSeenFirst)[0];
      speechRef.current.post(text, via ? { via } : undefined);
      if (c) openSheet(c.id);
      return true;
    }
    if (s === "go back a step") {
      speechRef.current.post(text, via ? { via } : undefined);
      respond({ kind: "back" });
      return true;
    }
    const intent = readIntent(text, !!w);
    if (!intent) return false;
    const long = via !== "chip" && connectTokens(text).length > SHORT_WORDS;
    // where netti itself can answer, a real question is its to answer, with the walk riding along;
    // the walk keeps the short lines and its own replies
    if (!generous && long && intent.kind !== "rename") return false;
    speechRef.current.post(text, via ? { via } : undefined);
    respond(intent, long);
    return true;
  }, [enabled, current, respond, openSheet, named]);

  const fallback = React.useCallback((text: string, via?: "chip") => {
    speechRef.current.post(text, via ? { via } : undefined);
    const w = current();
    say({ text: PREVIEW_FALLBACK, replies: w ? ["it isn’t showing up", "skip ahead"] : ["connect Claude", "connect ChatGPT", "what is mcp?"] });
  }, [current, say]);

  const context = React.useCallback(() => {
    const w = current();
    if (!w) return null;
    return { tool: w.tool, step: w.step, state: connOf(w)?.last_seen_at ? ("live" as const) : ("waiting" as const) };
  }, [current, connOf]);

  return {
    walks, conns, loaded, now, making,
    start, current, advance, rename, named, choose, stepDone, pasted, retryMake, pick, focusTool,
    hear, fallback, context, connOf, suggested, coach,
  };
}

/** a connection's state, in the words of a status line */
export function stateWord(c: Pick<Connection, "last_seen_at">, now: number): string {
  const s = connState(c, now);
  return s === "live" ? "reading your memory now" : s === "active" ? "active this week" : s === "quiet" ? "quiet lately" : "waiting for its first call";
}
